/**
 * One rule about finger sizes, shared by every path that can offer a size.
 *
 * A finger size belongs to a ring and to nothing else. SKU 45335 — a PAIR of
 * drop earrings, built on the engagement builder — was asking the customer for
 * a finger size, because the admin form seeds the full 29-rung UK ladder
 * whenever `engagement.sizes` is left empty (EngagementBuildForm.jsx →
 * DEFAULT_SIZES) and the storefront then shows whatever the recipe carries.
 * Nothing upstream stops the next pair of earrings doing the same, so the
 * storefront decides for itself.
 *
 * Kept in one file so the two size selectors — AddCart's variation path and
 * EngagementBuilder's recipe path — cannot drift, and mirrored on the server as
 * backend/utils/ringSize.js, which snapshots the size label onto an order line
 * and has to reach the same verdict.
 */

/**
 * A finger size AS THE CATALOGUE WRITES IT: the EU number with the UK letter in
 * brackets. Two admin screens seed the ladder and they punctuate it
 * differently — ProductEdit.jsx builds "44 (F ½)" and "72 (Z +2.5)",
 * EngagementBuildForm.jsx's DEFAULT_SIZES has "44 (F½)" and "72 (Z+2.5)" — so
 * both spacings have to match.
 *
 * The bracketed letter is what makes this safe to act on. A bare number would
 * be ambiguous: a necklace sized "45" means 45cm, and pulling a chain-length
 * selector off the page because it looked like ring size 45 would be a worse
 * bug than the one this fixes. Size 70 is the single rung with no UK letter,
 * and it rides along on the ladder test below rather than being classified
 * on its own.
 */
const FINGER_SIZE_RX =
  /^(?:3[6-9]|[4-7]\d|80)\s*\(\s*[A-Z]\s*(?:½|1\/2|\.5)?(?:\s*\+\s*\d+(?:\.\d+)?)?\s*\)$/i;

export const isFingerSizeLabel = (label) => FINGER_SIZE_RX.test(String(label ?? "").trim());

/**
 * Is this list of size labels a finger-size ladder? One rung carrying a UK
 * letter is enough — nothing else in the catalogue is written that way, and the
 * seeded ladder is 28 lettered rungs plus a bare "70".
 */
export const isFingerSizeLadder = (labels) => (labels || []).some(isFingerSizeLabel);

/**
 * "Earrings" contains "ring", so a plain substring test reads every stud and
 * hoop as a ring. Strip the earring words before asking. Spaced and hyphenated
 * spellings ("ear ring", "ear-rings") are covered because the catalogue is
 * hand-entered.
 */
export const mentionsRing = (text) =>
  String(text ?? "")
    .toLowerCase()
    .replace(/ear\s*-?\s*rings?/g, "")
    .includes("ring");

const NON_RING_RX =
  /pendant|necklace|choker|ear\s*-?\s*rings?|bracelet|bangle|anklet|chain|brooch|tiara|cuff\s*-?\s*link|charm|watch/i;

/** Everything on a product that says what KIND of piece it is. */
const pieceText = (product) =>
  [
    product?.Menu_Level1,
    product?.Menu_Level2,
    product?.Menu_Level3,
    ...(product?.Category || []).map((c) => c?.Category_Name || c?.category_name),
    product?.Product_Name,
  ]
    .filter(Boolean)
    .join(" ");

/**
 * True only on a clear signal — a piece named as a pendant, earring, bracelet…
 * with no mention of a ring anywhere on it.
 *
 * Deliberately one-sided: this answer is used to REMOVE a control, so silence
 * has to mean "leave it alone". A ring named without the word (Seraphina,
 * Aurelia) keeps its sizes because nothing here says it is anything else, and a
 * "Ring & Earring Set" keeps them because the ring word is still there.
 */
export const isNonRingProduct = (product) => {
  const text = pieceText(product);
  return NON_RING_RX.test(text) && !mentionsRing(text);
};

/**
 * The rule itself: a finger-size ladder is offered on a ring and nowhere else.
 * Any other kind of size — a hoop diameter, a chain length, Small/Medium — is
 * a real question and is left alone.
 */
export const offersFingerSize = (product, sizeLabels) =>
  !(isFingerSizeLadder(sizeLabels) && isNonRingProduct(product));
